<template>
  <AppShell>
    <section class="view on soa-view-profile" aria-labelledby="profile-title">
      <div class="pagehead">
        <div>
          <h1 id="profile-title">{{ t("prof_ident_h") }}</h1>
        </div>
      </div>

      <p v-if="loading" class="empty" role="status">{{ t("loading") }}</p>
      <p v-else-if="loadError" class="empty" role="alert">
        {{ loadError }} <button type="button" class="atbtn" @click="load">{{ t("account_retry") }}</button>
      </p>
      <p v-else-if="!currentBrand" class="empty">{{ t("prof_none") }}</p>
      <template v-else>
        <section class="prof-grp prof-ident" :class="{ 'is-editing': identityEditing }" :aria-label="t('prof_ident_h')">
          <form class="prof-ident-form" :aria-busy="identSaving ? 'true' : 'false'" @submit.prevent="saveIdentity">
            <div class="prof-f">
              <label class="prof-l" for="prof-brand-name">{{ t("prof_name_label") }}</label>
              <div v-if="!identityEditing" class="prof-readonly-value">{{ identName || "—" }}</div>
              <input v-else id="prof-brand-name" ref="nameInput" v-model="identName" class="prof-in prof-txt" type="text" maxlength="80" required :aria-describedby="renaming ? 'prof-name-caution prof-name-hint' : 'prof-name-caution'">
              <div v-if="identityEditing" id="prof-name-caution" class="prof-name-caution">{{ t("prof_name_caution") }}</div>
              <div v-if="identityEditing && renaming" id="prof-name-hint" class="prof-slug-hint">{{ t("prof_name_rename_hint", { old: currentBrand.brand }) }}</div>
            </div>
            <div class="prof-f">
              <span class="prof-l" id="prof-alias-label">{{ t("alias_line") }}</span>
              <div v-if="!identityEditing" class="prof-alias prof-alias-view" role="group" aria-labelledby="prof-alias-label">
                <ul v-if="identAliases.length" class="prof-alias-chips">
                  <li v-for="(alias, index) in identAliases" :key="`${index}-${alias}`" class="prof-alias-chip">
                    <span class="prof-alias-name">{{ alias }}</span>
                  </li>
                </ul>
                <span v-else class="prof-empty-value">—</span>
              </div>
              <div v-else class="prof-alias" role="group" aria-labelledby="prof-alias-label" aria-describedby="prof-alias-hint">
                <ul v-if="identAliases.length" class="prof-alias-chips">
                  <li v-for="(alias, index) in identAliases" :key="`${index}-${alias}`" class="prof-alias-chip">
                    <span class="prof-alias-name">{{ alias }}</span>
                    <button v-if="identityEditing" type="button" class="prof-alias-rm" :aria-label="t('prof_alias_remove', { name: alias })" @click="removeAlias(index)">×</button>
                  </li>
                </ul>
                <div v-if="identityEditing" class="prof-alias-addrow">
                  <input v-model="aliasDraft" class="prof-in prof-alias-in" type="text" maxlength="60" :placeholder="t('prof_alias_ph')" :aria-label="t('prof_alias_ph')" :disabled="identAliases.length >= aliasLimit" @keydown.enter.prevent="addAlias">
                  <button type="button" class="prof-add prof-alias-add" :disabled="identAliases.length >= aliasLimit || !aliasDraft.trim()" @click="addAlias">{{ t("prof_alias_add") }}</button>
                </div>
                <div v-if="identityEditing && suggestionChoices.length" class="prof-alias-sugs">
                  <span class="prof-alias-sug-l">{{ t("prof_suggest_label") }}:</span>
                  <button v-for="choice in suggestionChoices" :key="choice" type="button" class="prof-alias-sug" @click="addSuggestion(choice)">+ {{ choice }}</button>
                </div>
              </div>
              <div v-if="identityEditing" id="prof-alias-hint" class="prof-slug-hint">{{ t("prof_alias_hint", { n: aliasLimit }) }}</div>
            </div>
            <div class="prof-ident-foot">
              <p v-if="identError" class="form-error" role="alert">{{ identError }}</p>
              <p class="sr-only" aria-live="polite">{{ identStatus }}</p>
              <button v-if="!identityEditing" type="button" class="prof-ident-edit" @click="beginIdentityEdit">{{ t("prof_ident_edit") }}</button>
              <template v-else>
                <button type="button" class="prof-ident-cancel" :disabled="identSaving" @click="cancelIdentityEdit">{{ t("prof_ident_cancel") }}</button>
                <button type="submit" class="btn prof-savebtn" :disabled="identSaving">{{ identSaving ? t("prof_ident_saving") : t("prof_ident_save") }}</button>
              </template>
            </div>
          </form>
        </section>

        <div class="pagehead">
          <div>
            <h1 id="profile-title">{{ t("prof_h") }} -- {{ data?.brand_name || currentBrand.brand }}</h1>
            <p class="sub">{{ t("prof_sub") }}</p>
          </div>
        </div>

        <form class="profile-form" :aria-busy="saving ? 'true' : 'false'" @submit.prevent="save">
        <div class="prof-top">
          <div class="prof-meter" role="meter" :aria-label="t('prof_meter')" aria-valuemin="0" aria-valuemax="100" :aria-valuenow="completeness" :aria-valuetext="t('prof_complete', { p: completeness })">
            <div class="prof-bar" aria-hidden="true"><i :style="{ width: `${completeness}%` }"></i></div>
            <span class="prof-pct">{{ t("prof_complete", { p: completeness }) }}</span>
          </div>
          <button type="submit" class="btn prof-savebtn" :disabled="saving">{{ saving ? t("prof_saving") : t("prof_save") }}</button>
        </div>

        <section v-for="group in groups" :key="group" class="prof-grp" :aria-labelledby="`profile-group-${group}`">
          <div :id="`profile-group-${group}`" class="prof-grp-h">{{ groupLabel(group) }}</div>
          <div v-for="field in fieldsFor(group)" :key="field.id" class="prof-f">
            <label v-if="field.type === 'text'" class="prof-l" :for="`profile-${field.id}`">
              {{ fieldLabel(field) }}<template v-if="field.required">&nbsp;<em class="prof-req">{{ t("prof_req") }}</em></template>
            </label>
            <span v-else class="prof-l" :id="`profile-label-${field.id}`">
              {{ fieldLabel(field) }}<template v-if="field.required">&nbsp;<em class="prof-req">{{ t("prof_req") }}</em></template>
            </span>

            <template v-if="field.id === 'target_country'">
              <select :id="`profile-${field.id}`" class="prof-in prof-cc" :value="countryChoice" @change="setCountryChoice">
                <option v-for="country in countries" :key="country" :value="country">{{ country }}</option>
                <option value="__other__">{{ t("prof_other") }}</option>
              </select>
              <input v-if="countryChoice === '__other__'" v-model="form.target_country" class="prof-in prof-cc-other" type="text" :maxlength="field.max || undefined" :aria-label="t('prof_other_country')" :placeholder="t('prof_other_ph')">
            </template>

            <template v-else-if="field.id === 'website'">
              <input :id="`profile-${field.id}`" v-model="form[field.id]" class="prof-in prof-txt" type="text" inputmode="url" autocomplete="url" :maxlength="field.max || undefined" :placeholder="t('prof_ph_website')" :aria-describedby="`profile-${field.id}-hint`" :required="field.required">
              <div :id="`profile-${field.id}-hint`" class="prof-slug-hint">{{ t("prof_website_hint") }}</div>
            </template>

            <div v-else-if="field.id === 'slug_pref'" class="prof-slug-wrap">
              <div class="prof-slug-control" :class="{ 'is-locked': field.locked }">
                <span class="prof-slug-prefix">https://</span>
                <input :id="`profile-${field.id}`" v-model="form[field.id]" class="prof-in prof-txt" :class="{ 'prof-locked': field.locked }" type="text" minlength="3" :maxlength="field.max || undefined" pattern="[a-z0-9](?:[a-z0-9\-]{1,38}[a-z0-9])" :placeholder="t('prof_slug_placeholder')" :title="t('prof_slug_pattern')" :readonly="field.locked" :aria-readonly="field.locked ? 'true' : undefined">
                <span class="prof-slug-suffix">.{{ field.suffix || "soarank.app" }}</span>
              </div>
              <div class="prof-slug-hint">{{ t(field.locked ? "prof_slug_locked" : "prof_slug_hint") }}</div>
            </div>

            <input v-else-if="field.type === 'text'" :id="`profile-${field.id}`" v-model="form[field.id]" class="prof-in prof-txt" :type="inputType(field)" :inputmode="inputMode(field)" :autocomplete="autocomplete(field)" :maxlength="field.max || undefined" :pattern="inputPattern(field)" :placeholder="fieldPlaceholder(field)" :title="inputTitle(field)" :required="field.required" @input="validateTextField(field, $event)">

            <div v-else-if="field.type === 'bool'" class="prof-bool" role="group" :aria-labelledby="`profile-label-${field.id}`">
              <button type="button" class="bp-pill prof-yes" :class="{ on: form[field.id] === true }" :aria-pressed="form[field.id] === true" @click="toggleBool(field.id, true)">{{ t("prof_yes") }}</button>
              <button type="button" class="bp-pill prof-no" :class="{ on: form[field.id] === false }" :aria-pressed="form[field.id] === false" @click="toggleBool(field.id, false)">{{ t("prof_no") }}</button>
            </div>

            <div v-else-if="field.type === 'urls' || field.type === 'texts'" class="prof-list" :aria-labelledby="`profile-label-${field.id}`">
              <div v-for="(_, index) in form[field.id]" :key="index" class="prof-row">
                <input v-model="form[field.id][index]" class="prof-in prof-li" :type="field.type === 'urls' ? 'url' : 'text'" :inputmode="field.type === 'urls' ? 'url' : undefined" :maxlength="field.max || undefined" :placeholder="field.type === 'urls' ? t('prof_ph_url') : t('prof_ph_text')" :required="field.required && index === 0" :aria-label="t('prof_item_n', { label: fieldLabel(field), n: index + 1 })">
                <button type="button" class="prof-rm" :aria-label="t('prof_remove_n', { label: fieldLabel(field), n: index + 1 })" @click="removeRow(field.id, index)">×</button>
              </div>
              <button type="button" class="prof-add" @click="addRow(field)">{{ t("prof_add") }}</button>
            </div>

            <div v-else-if="field.type === 'quotes'" class="prof-list prof-quotes" :aria-labelledby="`profile-label-${field.id}`">
              <div v-for="(_, index) in form[field.id]" :key="index" class="prof-row prof-2">
                <input v-model="form[field.id][index].quote" class="prof-in prof-q-quote" type="text" :maxlength="field.max || undefined" :placeholder="t('prof_ph_quote')" :required="field.required && index === 0" :aria-label="t('prof_quote_n', { n: index + 1 })">
                <input v-model="form[field.id][index].author" class="prof-in prof-q-author" type="text" maxlength="120" :placeholder="t('prof_ph_author')" :aria-label="t('prof_author_n', { n: index + 1 })">
                <button type="button" class="prof-rm" :aria-label="t('prof_remove_n', { label: fieldLabel(field), n: index + 1 })" @click="removeRow(field.id, index)">×</button>
              </div>
              <button type="button" class="prof-add" @click="addRow(field)">{{ t("prof_add") }}</button>
            </div>

            <div v-else-if="field.type === 'facts'" class="prof-list prof-facts" :aria-labelledby="`profile-label-${field.id}`">
              <div v-for="(_, index) in form[field.id]" :key="index" class="prof-row prof-2">
                <input v-model="form[field.id][index].value" class="prof-in prof-fact-value" type="text" maxlength="60" :placeholder="t('prof_ph_value')" :required="field.required && index === 0" :aria-label="t('prof_fact_value_n', { n: index + 1 })">
                <input v-model="form[field.id][index].label" class="prof-in prof-fact-label" type="text" :maxlength="field.max || undefined" :placeholder="t('prof_ph_label')" :required="field.required && index === 0" :aria-label="t('prof_fact_label_n', { n: index + 1 })">
                <button type="button" class="prof-rm" :aria-label="t('prof_remove_n', { label: fieldLabel(field), n: index + 1 })" @click="removeRow(field.id, index)">×</button>
              </div>
              <button type="button" class="prof-add" @click="addRow(field)">{{ t("prof_add") }}</button>
            </div>
          </div>
        </section>
        <p v-if="saveError" class="form-error" role="alert">{{ saveError }}</p>
        <p class="sr-only" aria-live="polite">{{ status }}</p>
      </form>
      </template>
    </section>
  </AppShell>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";

import { aliasSuggest, brandProfile, saveBrandProfile, setBrandAliases, setBrandEntity } from "../api/brands";
import AppShell from "../layout/AppShell.vue";
import { soaToast } from "../legacy/soaToast.js";
import { brandState, currentBrand, loadBrands } from "../stores/brands";
import { useI18n } from "../useI18n";

const { lang, t } = useI18n();
const countries = ["United States", "Canada", "United Kingdom", "Australia", "Germany", "France", "Japan", "Singapore", "India", "Brazil", "Mexico", "Netherlands", "Spain", "Italy", "United Arab Emirates", "New Zealand", "Ireland", "Sweden", "Switzerland"];
const DEFAULT_COUNTRY = countries[0];
const data = ref(null), loading = ref(true), saving = ref(false), loadError = ref(""), saveError = ref(""), status = ref(""), countryOther = ref(false);
const form = reactive({});
let controller;

const aliasLimit = 10;
const identName = ref(""), identAliases = ref([]), aliasDraft = ref(""), suggestions = ref([]);
const identSaving = ref(false), identError = ref(""), identStatus = ref("");
const identityEditing = ref(false), nameInput = ref(null);

const fields = computed(() => data.value?.library?.fields || []);
const groups = computed(() => data.value?.library?.groups || [...new Set(fields.value.map((field) => field.group))]);
const completeness = computed(() => data.value?.completeness || 0);
const countryChoice = computed(() => countryOther.value ? "__other__" : countries.includes(form.target_country) ? form.target_country : "United States");

function fieldsFor(group) { return fields.value.filter((field) => field.group === group); }
function fieldLabel(field) {
  const labels = field.label || {};
  return labels[lang.value] || labels.en || field.id;
}
function groupLabel(group) {
  const key = `grp_${group}`;
  const translated = t(key);
  return translated === key ? String(group || "").replaceAll("_", " ") : translated;
}
function blankRow(type) { return type === "quotes" ? { quote: "", author: "" } : type === "facts" ? { value: "", label: "" } : ""; }
function inputType(field) { return field.id === "contact_email" ? "email" : field.id === "contact_phone" ? "tel" : "text"; }
function inputMode(field) { return field.id === "contact_email" ? "email" : field.id === "contact_phone" ? "tel" : field.id === "website" ? "url" : undefined; }
function autocomplete(field) { return field.id === "contact_email" ? "email" : field.id === "contact_phone" ? "tel" : field.id === "website" ? "url" : undefined; }
function inputPattern(field) { return field.id === "contact_phone" ? "[+0-9\\(\\) .\\-]{7,60}" : undefined; }
function inputTitle(field) { return field.id === "contact_phone" ? t("prof_phone_hint") : undefined; }
function fieldPlaceholder(field) {
  return {
    website: t("prof_ph_website"),
    contact_email: t("prof_ph_email"),
    contact_phone: t("prof_ph_phone"),
    hours: t("prof_ph_hours"),
    service_area: t("prof_ph_area"),
  }[field.id];
}
function validateTextField(field, event) {
  if (field.id !== "contact_phone") return;
  const input = event.currentTarget;
  const valid = !input.value || (/^[+0-9() .-]+$/.test(input.value) && (input.value.match(/\d/g) || []).length >= 7);
  input.setCustomValidity(valid ? "" : inputTitle(field));
}

function aliasKey(value) { return String(value || "").trim().toLowerCase(); }

const renaming = computed(() => aliasKey(identName.value) !== aliasKey(currentBrand.value?.brand));
const suggestionChoices = computed(() => {
  const taken = new Set(identAliases.value.map(aliasKey));
  const name = aliasKey(identName.value);
  return suggestions.value.map((value) => String(value).trim()).filter((value) => {
    const key = aliasKey(value);
    return value.length >= 2 && key !== name && !taken.has(key);
  }).slice(0, 6);
});

function setIdent() {
  const brand = currentBrand.value;
  identName.value = brand?.brand || "";
  identAliases.value = Array.isArray(brand?.aliases) ? brand.aliases.map(String) : [];
  aliasDraft.value = "";
  identError.value = "";
  identStatus.value = "";
  identityEditing.value = false;
  suggestions.value = [];
  loadSuggestions();
}

function beginIdentityEdit() {
  identityEditing.value = true;
  identError.value = "";
  identStatus.value = "";
  nextTick(() => nameInput.value?.focus());
}

function cancelIdentityEdit() {
  setIdent();
}

async function loadSuggestions() {
  const brand = currentBrand.value;
  if (!brand) return;
  try {
    const result = await aliasSuggest(brand.brand || "", brand.url || "");
    suggestions.value = Array.isArray(result.aliases) ? result.aliases : [];
  } catch {
    suggestions.value = [];
  }
}

function addAlias() {
  const value = aliasDraft.value.trim().replace(/\s+/g, " ");
  if (!value) return;
  if (value.length < 2) { identError.value = t("prof_alias_short"); return; }
  if (identAliases.value.length >= aliasLimit) { identError.value = t("prof_alias_max", { n: aliasLimit }); return; }
  const key = aliasKey(value);
  if (key === aliasKey(identName.value) || identAliases.value.some((alias) => aliasKey(alias) === key)) {
    identError.value = t("prof_alias_dup");
    return;
  }
  identError.value = "";
  identAliases.value.push(value);
  aliasDraft.value = "";
}

function addSuggestion(value) {
  if (identAliases.value.length >= aliasLimit) { identError.value = t("prof_alias_max", { n: aliasLimit }); return; }
  identError.value = "";
  identAliases.value.push(String(value).trim());
}

function removeAlias(index) {
  identAliases.value.splice(index, 1);
  identError.value = "";
}

async function saveIdentity() {
  const brand = currentBrand.value;
  if (!brand || identSaving.value) return;
  const newName = identName.value.trim().replace(/\s+/g, " ").slice(0, 80);
  if (!newName) return;
  const seen = new Set();
  const wanted = [];
  for (const raw of identAliases.value) {
    const value = String(raw).trim().replace(/\s+/g, " ").slice(0, 60);
    const key = aliasKey(value);
    if (value.length < 2 || key === aliasKey(newName) || seen.has(key)) continue;
    seen.add(key);
    wanted.push(value);
  }
  const oldName = brand.brand || "";
  const renamed = aliasKey(newName) !== aliasKey(oldName);
  // 改名时旧名必须留在别名里（后端 /entity 也会自动补）；先发 /aliases 再发 /entity，
  // 否则 /aliases 的整体替换会把旧名别名抹掉。
  const finalAliases = renamed && oldName.length >= 2 && !seen.has(aliasKey(oldName)) ? [oldName, ...wanted] : wanted;
  identSaving.value = true;
  identError.value = "";
  identStatus.value = "";
  try {
    const aliasResult = await setBrandAliases(brand.id, finalAliases);
    let stored = Array.isArray(aliasResult.aliases) ? aliasResult.aliases : finalAliases;
    if (renamed) {
      const result = await setBrandEntity(brand.id, newName);
      if (result.taken) {
        identError.value = t("prof_name_taken", { name: result.conflict || newName });
        return;
      }
      if (Array.isArray(result.aliases)) stored = result.aliases;
    }
    const item = brandState.items.find((entry) => String(entry.id) === String(brand.id));
    if (item) {
      item.brand = renamed ? newName : oldName;
      item.aliases = stored;
    }
    if (data.value) data.value = { ...data.value, brand_name: renamed ? newName : oldName };
    identName.value = renamed ? newName : oldName;
    identAliases.value = stored.map(String);
    identityEditing.value = false;
    identStatus.value = t("prof_ident_saved");
    soaToast(t("prof_ident_saved"), "", "ok");
    loadSuggestions();
  } catch (caught) {
    identError.value = caught.message || t("prof_ident_error");
  } finally {
    identSaving.value = false;
  }
}

function setForm(profile = {}) {
  Object.keys(form).forEach((key) => delete form[key]);
  fields.value.forEach((field) => {
    const value = profile[field.id];
    if (["urls", "texts", "quotes", "facts"].includes(field.type)) {
      form[field.id] = Array.isArray(value) && value.length ? value.map((item) => typeof item === "object" ? { ...item } : item) : [blankRow(field.type)];
    } else if (field.type === "bool") {
      form[field.id] = typeof value === "boolean" ? value : null;
    } else {
      form[field.id] = field.id === "target_country" ? String(value ?? DEFAULT_COUNTRY) : String(value ?? "");
    }
  });
  countryOther.value = Boolean(form.target_country) && !countries.includes(form.target_country);
}

function setCountryChoice(event) {
  countryOther.value = event.target.value === "__other__";
  form.target_country = countryOther.value ? "" : event.target.value;
}
function toggleBool(id, value) { form[id] = form[id] === value ? null : value; }
function addRow(field) { form[field.id].push(blankRow(field.type)); }
function removeRow(id, index) {
  if (form[id].length > 1) form[id].splice(index, 1);
  else form[id][0] = typeof form[id][0] === "object" ? Object.fromEntries(Object.keys(form[id][0]).map((key) => [key, ""])) : "";
}

function payload() {
  const out = {};
  fields.value.forEach((field) => {
    const value = form[field.id];
    if (field.type === "bool") {
      if (typeof value === "boolean") out[field.id] = value;
    } else if (field.type === "urls" || field.type === "texts") {
      const values = value.map((item) => String(item).trim()).filter(Boolean).map((item) => field.type === "urls" && !/^https?:\/\//i.test(item) ? `https://${item.replace(/^\/+/, "")}` : item);
      if (values.length) out[field.id] = values;
    } else if (field.type === "quotes") {
      const values = value.map((item) => ({ quote: item.quote.trim(), author: item.author.trim() })).filter((item) => item.quote);
      if (values.length) out[field.id] = values;
    } else if (field.type === "facts") {
      const values = value.map((item) => ({ value: item.value.trim(), label: item.label.trim() })).filter((item) => item.value && item.label);
      if (values.length) out[field.id] = values;
    } else {
      const text = String(value).trim();
      if (text) out[field.id] = field.id === "slug_pref" ? text.toLowerCase() : field.id === "website" && !/^https?:\/\//i.test(text) ? `https://${text.replace(/^\/+/, "")}` : text;
    }
  });
  return out;
}

async function load() {
  controller?.abort();
  controller = new AbortController();
  loading.value = true;
  loadError.value = "";
  saveError.value = "";
  data.value = null;
  try {
    await loadBrands({ signal: controller.signal });
    setIdent();
    if (currentBrand.value) {
      data.value = await brandProfile(currentBrand.value.id, { signal: controller.signal });
      setForm(data.value.profile);
    }
  } catch (caught) {
    if (caught.name !== "AbortError") loadError.value = caught.message || t("prof_load_error");
  } finally {
    loading.value = false;
  }
}

async function save() {
  if (!currentBrand.value || saving.value) return;
  saving.value = true;
  saveError.value = "";
  status.value = "";
  try {
    const result = await saveBrandProfile(currentBrand.value.id, payload());
    data.value = { ...data.value, ...result };
    setForm(data.value.profile);
    status.value = t("prof_saved");
    soaToast(t("prof_saved"), "", "ok");
  } catch (caught) {
    const code = caught.payload?.error;
    saveError.value = code === "bad_website" ? t("prof_bad_website") : code === "bad_slug" ? t("prof_bad_slug") : (caught.message || t("prof_save_error"));
  } finally {
    saving.value = false;
  }
}

onMounted(load);
watch(() => currentBrand.value?.id, (next, previous) => {
  if (next && previous != null && next !== previous) load();
});
onBeforeUnmount(() => controller?.abort());
</script>
